/**
 * Iter XX.48 — Reusable currency formatter.
 *
 * ONE source of truth for how monetary values are rendered anywhere in
 * the app. The backend ledger stays in integer minor units (paise) —
 * this file only maps display metadata (currency code + amount) into a
 * localised string via `Intl.NumberFormat`.
 *
 * Resolution order for the DISPLAY currency:
 *   1. explicit `currency` argument (backend-authoritative)
 *   2. `partner.display_currency` (partner preference)
 *   3. `partner.display_country` / `partner.country` (ISO alpha-2)
 *   4. `navigator.language` locale → currency
 *   5. USD fallback
 *
 * Every amount is expected in *minor units of the SAME currency it is
 * being displayed in*. If you receive `amount_minor` in INR paise but
 * want to render CAD, the backend must convert first (see
 * /partners/me/wallet `display.amounts_minor`).
 */

// ISO-3166-1 alpha-2 → ISO-4217. Mirrors backend/currency_display.py.
const COUNTRY_CURRENCY = {
  IN: "INR",
  CA: "CAD",
  US: "USD",
  GB: "GBP", UK: "GBP",
  AU: "AUD",
  NZ: "NZD",
  SG: "SGD",
  AE: "AED",
  ZA: "ZAR",
  JP: "JPY",
  CH: "CHF",
  DE: "EUR", FR: "EUR", IE: "EUR", IT: "EUR", ES: "EUR",
  NL: "EUR", BE: "EUR", AT: "EUR", PT: "EUR", GR: "EUR",
  FI: "EUR", LU: "EUR",
};

const MINOR_UNITS_PER_MAJOR = {
  // Everything we support is 1/100 except JPY.
  JPY: 1,
};

const CURRENCY_LOCALE = {
  INR: "en-IN",
  USD: "en-US",
  CAD: "en-CA",
  GBP: "en-GB",
  AUD: "en-AU",
  NZD: "en-NZ",
  EUR: "en-IE",
  SGD: "en-SG",
  AED: "en-AE",
  ZAR: "en-ZA",
  JPY: "ja-JP",
  CHF: "de-CH",
};

/** Resolve the display currency for a partner-like object. */
export function resolveCurrency({ currency, partner, defaultCurrency = "USD" } = {}) {
  if (currency) return String(currency).toUpperCase();
  const explicit = partner?.display_currency || partner?.display_currency_pref;
  if (explicit) return String(explicit).toUpperCase();
  const country = partner?.display_country || partner?.country;
  if (country) {
    const cc = String(country).trim().toUpperCase().slice(0, 2);
    if (COUNTRY_CURRENCY[cc]) return COUNTRY_CURRENCY[cc];
  }
  // Browser locale hint.
  try {
    const loc = (navigator?.language || "").toLowerCase();
    const region = loc.split(/[-_]/)[1];
    if (region) {
      const cc = region.toUpperCase();
      if (COUNTRY_CURRENCY[cc]) return COUNTRY_CURRENCY[cc];
    }
  } catch { /* SSR — ignore */ }
  return defaultCurrency;
}

/** Convert integer minor units → major float. */
export function minorToMajor(minor, currency) {
  const per = MINOR_UNITS_PER_MAJOR[currency] ?? 100;
  return Number(minor ?? 0) / per;
}

/**
 * Format a minor-unit amount as a currency string.
 * @param {number} minor  Integer amount in minor units OF THE DISPLAY currency.
 * @param {object} opts
 * @param {string} [opts.currency]  ISO-4217 code. If omitted, resolved from opts.partner.
 * @param {object} [opts.partner]   Partner profile used for resolution.
 * @param {string} [opts.locale]    Override the locale.
 * @param {number} [opts.maximumFractionDigits]
 */
export function formatMinor(minor, opts = {}) {
  const currency = resolveCurrency(opts);
  const locale = opts.locale || CURRENCY_LOCALE[currency] || navigator?.language || "en-US";
  const major = minorToMajor(minor, currency);
  const fractionDigits = MINOR_UNITS_PER_MAJOR[currency] === 1 ? 0 : (opts.maximumFractionDigits ?? 2);
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: fractionDigits,
    }).format(major);
  } catch {
    return `${currency} ${major.toFixed(fractionDigits)}`;
  }
}

/**
 * Format a MAJOR-unit float (already in the display currency).
 * Useful for the legacy `wallet.available` fields that ship as floats.
 */
export function formatMajor(major, opts = {}) {
  const currency = resolveCurrency(opts);
  const locale = opts.locale || CURRENCY_LOCALE[currency] || navigator?.language || "en-US";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(major ?? 0));
  } catch {
    return `${currency} ${Number(major ?? 0).toFixed(2)}`;
  }
}

/**
 * Return just the currency SYMBOL (₹, CA$, £…) for headings/labels
 * where you already own the number rendering.
 */
export function currencySymbol(opts = {}) {
  const currency = resolveCurrency(opts);
  const locale = opts.locale || CURRENCY_LOCALE[currency] || "en-US";
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    }).formatToParts(0);
    return (parts.find(p => p.type === "currency") || {}).value || currency;
  } catch {
    return currency;
  }
}

/**
 * Iter XX.267a — presentation-only fallback when neither the wallet
 * display block nor the ledger currency is known (typical for
 * non-partner users viewing `/earn` where the wallet API returns an
 * empty display). Derives a display ISO 4217 from the browser locale
 * without mutating any ledger value. Never persisted; never used to
 * infer a financial currency for a real balance.
 */
function _localeCurrencyFallback() {
  try {
    if (typeof Intl !== "undefined") {
      const opts = new Intl.NumberFormat().resolvedOptions();
      if (opts && typeof opts.currency === "string" && opts.currency.length === 3) {
        return opts.currency.toUpperCase();
      }
      const loc = String(opts.locale || "");
      const m = /[a-zA-Z]{2,3}-([A-Z]{2})/.exec(loc);
      if (m && COUNTRY_CURRENCY[m[1]]) return COUNTRY_CURRENCY[m[1]];
    }
  } catch { /* Intl unavailable — fall through */ }
  return "USD";
}

/** Read the wallet response's `display` block safely. */
export function pickDisplay(wallet) {
  const d = wallet?.display || {};
  const fallback = _localeCurrencyFallback();
  return {
    currency: d.currency || wallet?.wallet?.currency || fallback,
    canonical: d.canonical_currency || wallet?.wallet?.currency || fallback,
    converted: !!d.converted,
    fxStale: !!d.fx_stale,
    fxRate: d.fx_rate ?? null,
    fetchedAt: d.fx_fetched_at || null,
    provider: d.fx_provider || null,
    amounts: d.amounts_minor || {},
    reason: d.reason || null,
  };
}
